/* eslint-disable no-unused-vars */
import React from 'react';
import { ArrowUpRight, ChevronDown, CirclePlay, Zap } from 'lucide-react';
import type { WorkJourneyRow } from '../api';
import type { WorkJourneyItemRow } from '../api';
import { WorkItemRelationshipsPanel } from './WorkItemRelationshipsPanel';
import { StatusChip } from './Avatar';
import { formatRelativeDate } from '../utils/formatters';
import {
  actionLinkLabel,
  actionRecommendation
} from '../utils/journey-action-copy';
import {
  journeyFocusExpectation,
  journeyOperationalAction
} from '../utils/journey-radar';

interface JourneyDetailProps {
  journey: WorkJourneyRow;
  taskItems: WorkJourneyItemRow[];
  onCreateTaskRelationship: (
    sourceWorkJourneyItemId: string,
    targetWorkJourneyItemId: string
  ) => Promise<boolean>;
  onDeleteTaskRelationship: (relationshipId: string) => Promise<void>;
  dispatchableStageKeys: Set<string>;
  dispatchingStageKey?: string;
  onAcknowledge: (stageKey: string, label: string) => void;
  onDispatch: (stageKey: string, label: string) => void;
}

export function JourneyDetail({
  journey,
  taskItems,
  onCreateTaskRelationship,
  onDeleteTaskRelationship,
  dispatchableStageKeys,
  dispatchingStageKey,
  onAcknowledge,
  onDispatch
}: JourneyDetailProps) {
  const currentExpectation = journeyFocusExpectation(journey);
  const currentOperationalAction = journeyOperationalAction(journey);
  const directEvidenceIds = new Set(currentExpectation?.observedItemIds || []);
  const prerequisiteExpectations = journey.expectations.filter((expectation) =>
    currentExpectation?.dependsOn.includes(expectation.stageKey)
  );
  const blockedDownstream = journey.expectations.filter(
    (expectation) =>
      currentExpectation &&
      expectation.dependsOn.includes(currentExpectation.stageKey) &&
      !['complete', 'not_applicable'].includes(expectation.status)
  );
  const prerequisiteEvidenceIds = new Set(
    prerequisiteExpectations.flatMap(
      (expectation) => expectation.observedItemIds
    )
  );
  const relevantEvidence = journey.items
    .filter(
      (signal) =>
        directEvidenceIds.has(signal.workJourneyItemId) ||
        prerequisiteEvidenceIds.has(signal.workJourneyItemId)
    )
    .sort((left, right) =>
      (
        right.updated ||
        right.finishedAt ||
        right.startedAt ||
        right.created ||
        ''
      ).localeCompare(
        left.updated || left.finishedAt || left.startedAt || left.created || ''
      )
    )
    .slice(0, 3);
  const recentDispatch = journey.dispatchRuns?.[0];
  const currentResponsibilities =
    currentOperationalAction?.responsibilities || [];
  const policy = currentOperationalAction?.policy;

  const currentAction =
    !policy &&
    currentExpectation &&
    currentExpectation.waitingOn === 'human' &&
    currentExpectation.status === 'blocked' ? (
      dispatchableStageKeys.has(currentExpectation.stageKey) ? (
        <button
          type="button"
          className="journeyDispatchButton"
          disabled={dispatchingStageKey === currentExpectation.stageKey}
          onClick={() =>
            onDispatch(currentExpectation.stageKey, currentExpectation.label)
          }
        >
          <CirclePlay size={13} />
          {dispatchingStageKey === currentExpectation.stageKey
            ? 'Dispatching…'
            : 'Run next step'}
        </button>
      ) : (
        <button
          type="button"
          className="textButton"
          onClick={() =>
            onAcknowledge(currentExpectation.stageKey, currentExpectation.label)
          }
        >
          Mark complete
        </button>
      )
    ) : null;

  return (
    <div className="journeyInspector">
      <section className="journeyCurrentFocus">
        <div className="journeyInspectorHeading">
          <div>
            <span>Current focus</span>
            <strong>
              {policy
                ? 'Pull request approval'
                : currentExpectation?.label || 'Journey complete'}
            </strong>
          </div>
          {policy ? (
            <StatusChip
              tone={policy.state === 'changes_requested' ? 'warn' : 'neutral'}
            >
              {policy.state.replace('_', ' ')}
            </StatusChip>
          ) : currentExpectation ? (
            <StatusChip
              tone={
                currentExpectation.status === 'failed' ||
                currentExpectation.status === 'blocked'
                  ? 'warn'
                  : 'neutral'
              }
            >
              {currentExpectation.status.replace('_', ' ')}
            </StatusChip>
          ) : (
            <StatusChip tone="success">complete</StatusChip>
          )}
        </div>
        <p className="journeyFocusProblem">
          {currentOperationalAction?.cause ||
            currentExpectation?.detail ||
            'All applicable stages have completed.'}
        </p>
        {currentOperationalAction ? (
          <div className="journeyFocusRecommendation">
            <span>Do this next</span>
            <strong>{actionRecommendation(currentOperationalAction)}</strong>
            {currentOperationalAction.url ? (
              <a
                href={currentOperationalAction.url}
                target="_blank"
                rel="noreferrer"
              >
                {actionLinkLabel(currentOperationalAction)}
                <ArrowUpRight size={13} />
              </a>
            ) : null}
          </div>
        ) : null}
        <div className="journeyFocusFooter">
          <span>
            {policy
              ? `Required: ${currentOperationalAction?.requiredOwners.join(', ') || 'repository reviewers'}`
              : currentResponsibilities.length
                ? `Responsible: ${currentResponsibilities.map((responsibility) => responsibility.teamHandle || responsibility.identity?.displayName || responsibility.identity?.handle || 'Unmapped person').join(', ')}`
                : currentExpectation
                  ? currentExpectation.waitingOn === 'system'
                    ? 'Responsible queue: automation recovery'
                    : currentExpectation.waitingOn === 'external'
                      ? 'Responsible queue: external provider'
                      : 'No named owner'
                  : 'No outstanding dependency'}
          </span>
          {blockedDownstream.length ? (
            <span>
              Blocks:{' '}
              {blockedDownstream
                .map((expectation) => expectation.label)
                .join(', ')}
            </span>
          ) : null}
          {currentAction}
        </div>
        {recentDispatch ? (
          <div
            className={`journeyAutomationPulse journeyAutomationPulse--${recentDispatch.status}`}
          >
            <Zap size={14} />
            <div>
              <strong>
                {recentDispatch.actorType === 'system'
                  ? 'Automatic rule'
                  : 'Manual action'}{' '}
                · {recentDispatch.connectorType}
              </strong>
              <span>
                {recentDispatch.status === 'failed'
                  ? recentDispatch.errorMessage || 'The action failed'
                  : `${recentDispatch.connectorAction.replaceAll('_', ' ')} ${recentDispatch.status}`}
              </span>
            </div>
            <small>
              {recentDispatch.startedAt
                ? formatRelativeDate(recentDispatch.startedAt)
                : ''}
            </small>
          </div>
        ) : null}
      </section>
      {currentOperationalAction?.policy ? (
        <section className="journeyPolicyGate">
          <div className="journeyInspectorHeading">
            <div>
              <span>GitHub release gate</span>
              <strong>
                {currentOperationalAction.policy.state === 'unknown'
                  ? 'Policy visibility required'
                  : currentOperationalAction.policy.requiresCodeOwnerReview
                    ? 'Required reviewers and paths'
                    : 'Required pull request approvals'}
              </strong>
            </div>
            <StatusChip
              tone={
                currentOperationalAction.policy.state === 'changes_requested'
                  ? 'warn'
                  : 'neutral'
              }
            >
              {currentOperationalAction.policy.state.replace('_', ' ')}
            </StatusChip>
          </div>
          <div className="journeyPolicySummary">
            <span>
              <b>
                {currentOperationalAction.policy.requiredApprovalCount ?? '—'}
              </b>{' '}
              approvals required
            </span>
            <span>
              <b>
                {currentOperationalAction.policy.requiresCodeOwnerReview
                  ? currentOperationalAction.policy.requirements.reduce(
                      (total, requirement) =>
                        total + requirement.matchedPaths.length,
                      0
                    )
                  : '—'}
              </b>{' '}
              changed paths owned
            </span>
            <span>
              <b>
                {currentOperationalAction.policy.sourceSha?.slice(0, 7) || '—'}
              </b>{' '}
              policy revision
            </span>
          </div>
          {currentOperationalAction.policy.requiresCodeOwnerReview &&
          currentOperationalAction.policy.requirements.length ? (
            <div className="journeyPolicyRules">
              {currentOperationalAction.policy.requirements
                .slice(0, 4)
                .map((requirement) => (
                  <div
                    key={`${requirement.pattern}:${requirement.owners.map((value) => value.handle).join(':')}`}
                  >
                    <span
                      className={`journeyPolicyState journeyPolicyState--${requirement.state}`}
                      aria-hidden="true"
                    />
                    <span>
                      <strong>
                        {requirement.owners
                          .map((value) => `@${value.handle}`)
                          .join(', ')}
                      </strong>
                      <small>
                        {requirement.pattern} ·{' '}
                        {requirement.matchedPaths.slice(0, 2).join(', ')}
                        {requirement.matchedPaths.length > 2
                          ? ` +${requirement.matchedPaths.length - 2}`
                          : ''}
                      </small>
                    </span>
                    <b>{requirement.state.replace('_', ' ')}</b>
                  </div>
                ))}
            </div>
          ) : (
            <p className="journeyPolicyUnavailable">
              {currentOperationalAction.policy.state === 'unknown'
                ? 'The GitHub App cannot verify branch review protection. Check repository permissions before this release proceeds.'
                : currentOperationalAction.policy.requiresCodeOwnerReview
                  ? 'GitHub requires a code-owner review, but no changed path matched the current CODEOWNERS file.'
                  : 'GitHub is waiting for the required pull request approval count. No code-owner review is required by this branch.'}
            </p>
          )}
          <small className="journeyPolicySource">
            Evaluated from{' '}
            {currentOperationalAction.policy.sourcePath || 'branch protection'}{' '}
            on {currentOperationalAction.policy.baseRef || 'the base branch'}.
          </small>
        </section>
      ) : (
        <section className="journeyRecentEvidence">
          <div className="journeyInspectorHeading">
            <div>
              <span>Related activity</span>
              <strong>
                {directEvidenceIds.size
                  ? currentExpectation?.label
                  : 'Completed prerequisite'}
              </strong>
            </div>
          </div>
          <div className="journeyEvidencePreview">
            {relevantEvidence.map((signal) => (
              <div key={signal.workJourneyItemId}>
                <span
                  className={`journeyEvidenceState journeyEvidenceState--${signal.status}`}
                  aria-hidden="true"
                />
                <div>
                  <strong>{signal.title}</strong>
                  <small>
                    {directEvidenceIds.has(signal.workJourneyItemId)
                      ? `Linked to ${currentExpectation?.label}`
                      : `Completed prerequisite · ${prerequisiteExpectations.find((expectation) => expectation.observedItemIds.includes(signal.workJourneyItemId))?.label || 'upstream stage'}`}
                  </small>
                </div>
                {signal.url ? (
                  <a
                    href={signal.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Open ${signal.title}`}
                  >
                    <ArrowUpRight size={14} />
                  </a>
                ) : null}
              </div>
            ))}
            {!relevantEvidence.length ? (
              <div className="journeyEvidenceEmpty">
                <strong>No related activity found</strong>
                <small>
                  Nothing is linked to this stage or its prerequisite yet.
                </small>
              </div>
            ) : null}
          </div>
        </section>
      )}
      <WorkItemRelationshipsPanel
        journey={journey}
        taskItems={taskItems}
        onCreate={onCreateTaskRelationship}
        onDelete={onDeleteTaskRelationship}
      />
      <details className="journeyHistory">
        <summary>
          <span>
            <strong>Technical history</strong>
            <small>Stages, linked activity and automation attempts</small>
          </span>
          <ChevronDown size={15} />
        </summary>
        <div className="journeyHistoryBody">
          <section>
            <h4>Expected path</h4>
            <div className="journeyExpectationList">
              {journey.expectations.map((expectation) => (
                <div key={expectation.workJourneyExpectationId}>
                  <StatusChip
                    tone={
                      expectation.status === 'complete'
                        ? 'success'
                        : expectation.status === 'failed' ||
                            expectation.status === 'blocked'
                          ? 'warn'
                          : 'neutral'
                    }
                  >
                    {expectation.status.replace('_', ' ')}
                  </StatusChip>
                  <strong>{expectation.label}</strong>
                  <span>{expectation.detail}</span>
                  <small>
                    Waiting on {expectation.waitingOn}
                    {expectation.required ? '' : ' · optional'}
                  </small>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h4>All linked activity</h4>
            <div className="journeyEvidenceList">
              {journey.items.map((signal) => (
                <div key={signal.workJourneyItemId}>
                  <span>{signal.kind.replace('_', ' ')}</span>
                  <strong>{signal.title}</strong>
                  <small>
                    {[signal.externalRepo, signal.branch, signal.status]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                  {signal.url ? (
                    <a
                      href={signal.url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${signal.title}`}
                    >
                      <ArrowUpRight size={14} />
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
          </section>
          {journey.dispatchRuns?.length ? (
            <section>
              <h4>Delivery actions</h4>
              <div className="journeyDispatchHistory">
                {journey.dispatchRuns.map((run) => (
                  <div key={run.dispatchRunId}>
                    <span
                      className={`journeyEvidenceState journeyEvidenceState--${run.status === 'failed' ? 'failed' : run.status === 'success' ? 'success' : 'running'}`}
                    />
                    <div>
                      <strong>
                        {run.connectorAction.replaceAll('_', ' ')}
                      </strong>
                      <small>
                        {run.actorType === 'system' ? 'Automatic' : 'Manual'} ·{' '}
                        {run.connectorType}
                        {run.errorMessage ? ` · ${run.errorMessage}` : ''}
                      </small>
                    </div>
                    <time>
                      {run.startedAt ? formatRelativeDate(run.startedAt) : ''}
                    </time>
                  </div>
                ))}
              </div>
            </section>
          ) : null}
        </div>
      </details>
    </div>
  );
}
